import { BarcodeOutlined, InfoCircleOutlined, MailOutlined, PhoneOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import {
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Switch,
  Tabs,
  Typography,
  theme,
} from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type ReactQuill from 'react-quill-new';
import { useParams } from 'react-router';
import { CodeEditor } from '../../../components/CodeEditor.tsx';
import { MainContent } from '../../../components/MainContent';
import { RichTextEditor } from '../../../components/RichTextEditor.tsx';
import type { LabInput } from '../../../graphql/generated/graphql.ts';
import { serverSideEmailRule } from '../../../utils/helpers.ts';
import { LazyAiExamplesEditor } from '../ai/LazyAiExamplesEditor.tsx';
import { LabelPreviewModal } from '../labelPreview/LabelPreviewModal.tsx';
import { PlaceholderHelp } from '../labelPreview/PlaceholderHelp.tsx';
import { LAB_QUERY, UPDATE_LAB_MUTATION } from './Basedata.gql';

const { useToken } = theme;

export const Basedata: React.FC = () => {
  const { token } = useToken();
  const params = useParams<{ id: string }>();
  const [form] = useForm<LabInput>();
  const overrideRunningNumber = useWatch('overrideRunningNumber', form);
  const overrideInvoiceCounter = useWatch('overrideInvoiceCounter', form);
  const { message } = App.useApp();
  const quillRef = useRef<ReactQuill>(null);

  const { data, loading, refetch } = useQuery(LAB_QUERY, {
    variables: {
      id: params.id ?? '',
    },
    fetchPolicy: 'cache-and-network', // use cache here to update the name when the basedata changes
    pollInterval: 60000,
  });

  const [preview, setPreview] = useState<{ zpl: string; width: number; height: number } | null>(null);

  const [updateLabMutation] = useMutation(UPDATE_LAB_MUTATION);

  const update = async (values: LabInput) => {
    try {
      await updateLabMutation({
        variables: {
          id: params.id ?? '',
          input: {
            ...values,
            elgaOptOutText:
              // see https://github.com/slab/quill/issues/3957
              quillRef.current?.editor
                ?.getSemanticHTML()
                // see https://github.com/slab/quill/issues/4509
                ?.replace(/((?:&nbsp;)*)&nbsp;/g, '$1 ') ?? values.elgaOptOutText,
          },
        },
      });

      message.success('Die Labor-Basisdaten wurden erfolgreich aktualisiert');
      refetch();
    } catch {
      message.error('Die Labor-Basisdaten konnten nicht aktualisiert werden');
    }
  };

  const tooltip = {
    styles: { root: { width: 400, maxWidth: 400 } },
    overlay: <PlaceholderHelp labelType="ORDER" />,
    icon: <InfoCircleOutlined />,
  };

  useEffect(() => {
    form.setFieldsValue({
      name: data?.lab?.name ?? '',
      shortName: data?.lab?.shortName ?? '',
      longName: data?.lab?.longName ?? '',
      meAddress: data?.lab?.meAddress ?? '',
      city: data?.lab?.city ?? '',
      email: data?.lab?.email ?? '',
      fax: data?.lab?.fax ?? '',
      phone: data?.lab?.phone ?? '',
      street: data?.lab?.street ?? '',
      zip: data?.lab?.zip ?? '',
      zplLabelTemplate: data?.lab?.zplLabelTemplate ?? '',
      zplLabelWidth: data?.lab?.zplLabelWidth ?? 0,
      zplLabelHeight: data?.lab?.zplLabelHeight ?? 0,
      zplSpecialLabelTemplate: data?.lab?.zplSpecialLabelTemplate ?? '',
      zplSpecialLabelWidth: data?.lab?.zplSpecialLabelWidth ?? 0,
      zplSpecialLabelHeight: data?.lab?.zplSpecialLabelHeight ?? 0,
      newReorderRunningNumber: data?.lab?.newReorderRunningNumber ?? false,
      diagnoseRequired: data?.lab?.diagnoseRequired ?? false,
      pickupTransferDocument: data?.lab?.pickupTransferDocument ?? false,
      printPartnerLab1Params: data?.lab?.printPartnerLab1Params ?? false,
      printPartnerLab2Params: data?.lab?.printPartnerLab2Params ?? false,
      printPartnerLab3Params: data?.lab?.printPartnerLab3Params ?? false,
      printPartnerLab4Params: data?.lab?.printPartnerLab4Params ?? false,
      printPartnerLab5Params: data?.lab?.printPartnerLab5Params ?? false,
      pl1Name: data?.lab?.pl1Name,
      pl2Name: data?.lab?.pl2Name,
      pl3Name: data?.lab?.pl3Name,
      pl4Name: data?.lab?.pl4Name,
      pl5Name: data?.lab?.pl5Name,
      runningNumber: data?.lab?.runningNumber ?? 0,
      overrideRunningNumber: false,
      invoiceCounter: data?.lab?.invoiceCounter ?? 0,
      overrideInvoiceCounter: false,
      elgaOptOut: data?.lab?.elgaOptOut ?? false,
      elgaOptOutText: data?.lab?.elgaOptOutText ?? '',
      aiEnabled: data?.lab?.aiEnabled ?? false,
      aiSystemPromptExtension: data?.lab?.aiSystemPromptExtension ?? '',
    });
  }, [form, data]);

  return (
    <MainContent>
      <Form layout="vertical" form={form} onFinish={update}>
        <Tabs
          defaultActiveKey="stammdaten"
          size="small"
          items={[
            {
              key: 'stammdaten',
              label: 'Stammdaten',
              forceRender: true,
              children: (
                <>
                  <Row gutter={[16, 0]}>
                    <Col span={8}>
                      <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true }]}>
                        <Input autoFocus />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="shortName" label="Kurzbezeichnung" rules={[{ required: true, whitespace: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="longName" label="Langbezeichnung" rules={[{ required: true, whitespace: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 0]}>
                    <Col span={8}>
                      <Form.Item name="street" label="Straße inkl. Hausnummer" rules={[{ required: true, whitespace: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="zip" label="PLZ" rules={[{ required: true, whitespace: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="city" label="Ort" rules={[{ required: true, whitespace: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 0]}>
                    <Col span={8}>
                      <Form.Item name="email" label="E-Mail" rules={[serverSideEmailRule]} validateDebounce={200}>
                        <Input prefix={<MailOutlined />} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="phone" label="Telefon">
                        <Input prefix={<PhoneOutlined />} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="fax" label="Fax">
                        <Input prefix={<PhoneOutlined />} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 0]}>
                    <Col span={8}>
                      <Form.Item name="meAddress" label="ME-Adresse" rules={[{ required: true, whitespace: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ),
            },
            {
              key: 'labelTemplate',
              label: 'Etiketten-Vorlage',
              forceRender: true,
              children: (
                <>
                  <Row gutter={[16, 0]}>
                    <Col span={12}>
                      <Form.Item
                        name="zplLabelWidth"
                        label="Breite ZPL-Etikett in mm für Vorschau"
                        rules={[{ type: 'integer', required: true, min: 10, max: 100, message: 'Bitte Zahl angeben' }]}
                      >
                        <InputNumber inputMode="numeric" required min={10} max={100} step={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="zplLabelHeight"
                        label="Höhe ZPL-Etikett in mm für Vorschau"
                        rules={[{ type: 'integer', required: true, min: 10, max: 100, message: 'Bitte Zahl angeben' }]}
                      >
                        <InputNumber inputMode="numeric" required min={10} max={100} step={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    name="zplLabelTemplate"
                    label={
                      <div>
                        <span>ZPL-Etiketten-Vorlage</span>{' '}
                        <Button
                          size="small"
                          icon={<BarcodeOutlined />}
                          onClick={() =>
                            setPreview({
                              zpl: form.getFieldValue('zplLabelTemplate'),
                              width: form.getFieldValue('zplLabelWidth'),
                              height: form.getFieldValue('zplLabelHeight'),
                            })
                          }
                        >
                          Vorschau
                        </Button>
                      </div>
                    }
                    rules={[{ required: true, whitespace: true }]}
                    tooltip={tooltip}
                  >
                    <CodeEditor mode="zplOrderLabel" height="300px" />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'specialLabelTemplate',
              label: 'Spezial-Etiketten-Vorlage',
              forceRender: true,
              children: (
                <>
                  <Row gutter={[16, 0]}>
                    <Col span={12}>
                      <Form.Item
                        name="zplSpecialLabelWidth"
                        label="Breite ZPL-Etikett in mm für Vorschau"
                        rules={[{ type: 'integer', required: true, min: 10, max: 100, message: 'Bitte Zahl angeben' }]}
                      >
                        <InputNumber inputMode="numeric" required min={10} max={100} step={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="zplSpecialLabelHeight"
                        label="Höhe ZPL-Etikett in mm für Vorschau"
                        rules={[{ type: 'integer', required: true, min: 10, max: 100, message: 'Bitte Zahl angeben' }]}
                      >
                        <InputNumber inputMode="numeric" required min={10} max={100} step={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    name="zplSpecialLabelTemplate"
                    label={
                      <div>
                        <span>ZPL-Etiketten-Vorlage</span>{' '}
                        <Button
                          size="small"
                          icon={<BarcodeOutlined />}
                          onClick={() =>
                            setPreview({
                              zpl: form.getFieldValue('zplSpecialLabelTemplate'),
                              width: form.getFieldValue('zplSpecialLabelWidth'),
                              height: form.getFieldValue('zplSpecialLabelHeight'),
                            })
                          }
                        >
                          Vorschau
                        </Button>
                      </div>
                    }
                    tooltip={tooltip}
                  >
                    <CodeEditor mode="zplOrderLabel" height="300px" />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'elgaOptOut',
              label: 'ELGA Opt-out',
              forceRender: true,
              children: (
                <Card
                  title="Situatives Opt-out (ELGA-Widerspruch im Anlassfall)"
                  size="small"
                  type="inner"
                  className={css`
                    margin-bottom: ${token.marginMD}px;
                  `}
                >
                  <Row gutter={[16, 0]}>
                    <Col span={12}>
                      <Form.Item name="elgaOptOut" valuePropName="checked" noStyle>
                        <Checkbox>
                          <div>Das Labor unterstützt das Opt-out</div>
                          <Typography.Text
                            className={css`
                              font-size: ${token.fontSizeSM}px;
                            `}
                            type="secondary"
                          >
                            Erweiterung der HL7-Schnittstelle ist implementiert
                          </Typography.Text>
                        </Checkbox>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="elgaOptOutText" label="Laborspezifischer Hinweistext für das Opt-out">
                        <RichTextEditor ref={quillRef} placeholder="Geben Sie den Inhalt an." />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ),
            },
            {
              key: 'settings',
              label: 'Einstellungen',
              forceRender: true,
              children: (
                <Row
                  gutter={[16, 16]}
                  className={css`
                    margin-bottom: ${token.marginSM}px;
                  `}
                >
                  <Col xs={24} lg={12}>
                    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                      <Card title="Allgemeines" size="small" type="inner">
                        <Space orientation="vertical">
                          <Form.Item name="newReorderRunningNumber" valuePropName="checked" noStyle>
                            <Checkbox>Neue Auftragsnummer für Nachforderungen</Checkbox>
                          </Form.Item>
                          <Form.Item name="pickupTransferDocument" valuePropName="checked" noStyle>
                            <Checkbox>Überweisungsschein-Dokument elektronisch übertragen</Checkbox>
                          </Form.Item>
                          <Form.Item name="diagnoseRequired" valuePropName="checked" noStyle>
                            <Checkbox>Diagnosepflicht</Checkbox>
                          </Form.Item>
                        </Space>
                      </Card>
                      <Card title="Laufende Auftragsnummer" size="small" type="inner">
                        <Row gutter={[16, 0]}>
                          <Col span={12}>
                            <Form.Item
                              name="runningNumber"
                              rules={[{ type: 'integer', required: true, min: 0, message: 'Bitte Zähler angeben' }]}
                            >
                              <InputNumber
                                className={css`
                                  width: 100%;
                                `}
                                precision={0}
                                min={0}
                                step={1}
                                disabled={!overrideRunningNumber}
                              />
                            </Form.Item>
                          </Col>
                          <Col>
                            <Form.Item name="overrideRunningNumber" valuePropName="checked" noStyle>
                              <Checkbox
                                className={css`
                                  margin-top: ${token.paddingXXS}px;
                                `}
                              >
                                Überschreiben
                              </Checkbox>
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                      <Card title="Laufende Rechnungsnummer" size="small" type="inner">
                        <Row gutter={[16, 0]}>
                          <Col span={12}>
                            <Form.Item
                              name="invoiceCounter"
                              rules={[{ type: 'integer', required: true, min: 0, message: 'Bitte Zähler angeben' }]}
                            >
                              <InputNumber
                                className={css`
                                  width: 100%;
                                `}
                                precision={0}
                                min={0}
                                step={1}
                                disabled={!overrideInvoiceCounter}
                              />
                            </Form.Item>
                          </Col>
                          <Col>
                            <Form.Item name="overrideInvoiceCounter" valuePropName="checked" noStyle>
                              <Checkbox
                                className={css`
                                  margin-top: ${token.paddingXXS}px;
                                `}
                              >
                                Überschreiben
                              </Checkbox>
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    </Space>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                      <Card title="Partnerlabor" size="small" type="inner">
                        <Space orientation="vertical">
                          <Form.Item name="printPartnerLab1Params" valuePropName="checked" noStyle>
                            <Checkbox>Partnerlabor 1 Parameter andrucken</Checkbox>
                          </Form.Item>
                          <Form.Item name="printPartnerLab2Params" valuePropName="checked" noStyle>
                            <Checkbox>Partnerlabor 2 Parameter andrucken</Checkbox>
                          </Form.Item>
                          <Form.Item name="printPartnerLab3Params" valuePropName="checked" noStyle>
                            <Checkbox>Partnerlabor 3 Parameter andrucken</Checkbox>
                          </Form.Item>
                          <Form.Item name="printPartnerLab4Params" valuePropName="checked" noStyle>
                            <Checkbox>Partnerlabor 4 Parameter andrucken</Checkbox>
                          </Form.Item>
                          <Form.Item name="printPartnerLab5Params" valuePropName="checked" noStyle>
                            <Checkbox>Partnerlabor 5 Parameter andrucken</Checkbox>
                          </Form.Item>
                        </Space>
                        <Divider />
                        <Form.Item label="Name Partnerlabor 1" layout="horizontal" colon={false} name="pl1Name">
                          <Input />
                        </Form.Item>
                        <Form.Item label="Name Partnerlabor 2" layout="horizontal" colon={false} name="pl2Name">
                          <Input />
                        </Form.Item>
                        <Form.Item label="Name Partnerlabor 3" layout="horizontal" colon={false} name="pl3Name">
                          <Input />
                        </Form.Item>
                        <Form.Item label="Name Partnerlabor 4" layout="horizontal" colon={false} name="pl4Name">
                          <Input />
                        </Form.Item>
                        <Form.Item label="Name Partnerlabor 5" layout="horizontal" colon={false} name="pl5Name">
                          <Input />
                        </Form.Item>
                      </Card>
                    </Space>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'aiConfig',
              label: 'KI-Konfiguration',
              forceRender: true,
              children: (
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item name="aiEnabled" label="KI-Assistent aktiviert" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      name="aiSystemPromptExtension"
                      label="System-Prompt Erweiterung"
                      tooltip="Labor-spezifischer Kontext für den KI-Assistenten, z.B. Spezialisierungen oder besondere Angebote"
                    >
                      <Input.TextArea rows={6} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <LazyAiExamplesEditor labId={params.id ?? ''} />
                  </Col>
                </Row>
              ),
            },
          ]}
        />
        <Form.Item noStyle shouldUpdate>
          {() => {
            const hasError = form.getFieldsError().some(({ errors }) => errors.length);
            return (
              <Flex gap="small" align="center">
                <Button htmlType="submit" type="primary" disabled={loading || hasError} icon={<SaveOutlined />}>
                  Speichern
                </Button>
                {hasError && <Typography.Text type="danger">Manche Felder sind nicht valide, bitte Fehler in Tabs checken</Typography.Text>}
              </Flex>
            );
          }}
        </Form.Item>
      </Form>
      <LabelPreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        labelType="ORDER"
        zpl={preview?.zpl ?? ''}
        width={preview?.width ?? 0}
        height={preview?.height ?? 0}
        fixedValues={{ LAB: form.getFieldValue('shortName') }}
      />
    </MainContent>
  );
};
